// hooks/useTabDeepLink.js — Deep link `?tab=` untuk halaman ber-tab.
//
// MASALAH YANG DIPECAHKAN
// QuickSearch (Ctrl+K) di Header menavigasi ke `${path}?tab=${tabId}`. Halaman
// yang mendukungnya selama ini hanya membaca parameter itu di *lazy initializer*
// `useState(() => …)`, yang berjalan SEKALI saat komponen mount.
//
// Tapi `DashboardContainer` memakai lazy mount lalu `display:none` — halaman
// TIDAK PERNAH di-unmount setelah pertama dibuka (lihat komentar "Lazy mount" di
// DashboardContainer). Akibatnya deep link hanya bekerja pada kunjungan PERTAMA
// seumur sesi login. Pencarian berikutnya ke tab lain pada halaman yang sama
// membuka halaman itu di tab lamanya, dan dari sisi user tampak seperti "hasil
// search-nya tidak berfungsi" — bukan seperti bug tab.
//
// Hook ini menambahkan sisi REAKTIF-nya. Initializer di masing-masing halaman
// TETAP DIPERTAHANKAN: tanpa itu, tab default sempat berkedip satu frame sebelum
// efek berjalan.
//
// KENAPA SATU HOOK, BUKAN DISALIN KE TIAP HALAMAN
// Ada 10 halaman ber-tab dengan bentuk state yang berbeda-beda (`activeTab`,
// `activeView`, `tab`, `view`, `active`, `activeKey`). Logika yang disalin
// sepuluh kali pasti menyimpang — pelajaran yang sudah berulang di codebase ini
// (THEME_PRESETS, hint DB vs sqlSeeds). Perbaikan berikutnya cukup di satu file.

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActiveDashboard } from '../context/ActiveDashboardContext';

/**
 * @param {string}   pageKey  key halaman di ROUTE_META (mis. 'aws_cost').
 * @param {function} isValid  (tabId) => boolean — validasi terhadap daftar tab
 *                            halaman itu. WAJIB: nilai asing di URL yang dipakai
 *                            mentah bisa membuat panel aktif undefined → blank.
 * @param {function} setTab   setter state tab halaman tsb.
 */
export default function useTabDeepLink(pageKey, isValid, setTab) {
  const [searchParams] = useSearchParams();
  const activePageKey  = useActiveDashboard();

  useEffect(() => {
    // Halaman lain yang masih ter-mount di balik `display:none` TIDAK boleh ikut
    // berganti tab saat URL berubah. Tanpa penjagaan ini, membuka tab AWS Cost
    // lewat search juga akan diam-diam menggeser tab Solman, Notes, dst.
    if (activePageKey !== pageKey) return;
    const p = searchParams.get('tab');
    if (p && isValid(p)) setTab(p);
    // isValid & setTab sengaja TIDAK masuk deps: keduanya biasanya fungsi inline
    // yang identitasnya berubah tiap render, sehingga efek akan berjalan terus
    // dan menimpa tab yang baru saja diklik user secara manual.
    //
    // Komentar disable ditulis POLOS, tanpa menyebut nama rule. Plugin
    // `react-hooks` tidak terdaftar di config ESLint project ini, dan menyebut
    // rule yang tidak dikenal justru MENGGAGALKAN build:
    //   Definition for rule 'react-hooks/exhaustive-deps' was not found
    // Bentuk polos inilah yang dipakai seluruh codebase (mis. DashboardContainer).
  }, [activePageKey, searchParams]); // eslint-disable-line
}
